import React, { useEffect, useRef } from 'react'
import { cn } from '../../lib/cn'

const sizeClass = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
  full: 'max-w-full sm:max-w-2xl',
}

/**
 * Modal accesible: role=dialog, aria-modal, cierre con Esc / overlay, foco atrapado.
 * Mantiene la firma { isOpen, onClose, title, children } usada en la app + extras.
 * @param {{ isOpen:boolean, onClose:()=>void, title?:React.ReactNode, description?:string,
 *           size?:'sm'|'md'|'lg'|'xl'|'2xl', footer?:React.ReactNode, closeOnOverlay?:boolean,
 *           fullscreenMobile?:boolean }} props
 * `fullscreenMobile`: en el celular el panel ocupa toda la pantalla, el cuerpo scrollea y el
 * pie (`footer`) queda fijo abajo, con el pulgar a mano; desde sm vuelve a ser una ventana.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  closeOnOverlay = true,
  fullscreenMobile = false,
  children,
}) {
  const panelRef = useRef(null)
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`).current

  // Mantener la última referencia de onClose sin re-disparar el efecto de apertura.
  // (Si onClose estuviera en las deps, cada render del padre re-ejecutaría el efecto
  //  y su setTimeout robaría el foco del input, rompiendo la escritura continua.)
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // foco inicial en el panel, solo al abrir (no en cada tecleo).
    const t = setTimeout(() => panelRef.current?.focus(), 0)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      clearTimeout(t)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-ink/50 backdrop-blur-sm animate-fade-in"
        onClick={closeOnOverlay ? onClose : undefined}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full bg-surface shadow-card-hover outline-none animate-slide-up',
          fullscreenMobile
            ? 'flex h-[100dvh] max-h-[100dvh] flex-col rounded-none sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl'
            : 'rounded-t-3xl sm:rounded-2xl',
          sizeClass[size]
        )}
      >
        {(title || onClose) && (
          <div className="flex shrink-0 items-start justify-between gap-4 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-hairline">
            <div>
              {title && (
                <h3 id={titleId} className="text-lg font-semibold text-ink">
                  {title}
                </h3>
              )}
              {description && <p className="mt-0.5 text-sm text-ink-soft">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="-mr-1.5 rounded-lg p-1.5 text-ink-muted hover:bg-slate-100 hover:text-ink transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className={cn('px-4 sm:px-6 py-3 sm:py-5 overflow-y-auto', fullscreenMobile ? 'min-h-0 flex-1' : 'max-h-[85vh] sm:max-h-[70vh]')}>{children}</div>
        {footer && <div className="shrink-0 px-4 sm:px-6 pt-3 sm:pt-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4 border-t border-hairline bg-slate-50/60 sm:rounded-b-2xl">{footer}</div>}
      </div>
    </div>
  )
}

export default Modal
